import React, { useRef } from "react";
import { Input } from "@/components/ui/InputDefault";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";

export interface SearchBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

/**
 * Expandable search bar component.
 * Shows a search icon when collapsed, expands to full input when clicked.
 */
export function SearchBar({
  searchTerm,
  onSearchChange,
  isExpanded,
  onExpandedChange,
}: SearchBarProps) {
  const { t } = usePlatformTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchClick = () => {
    onExpandedChange(true);
    // Focus the input after a brief delay to allow animation
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const handleSearchBlur = () => {
    // Only collapse if search is empty
    if (!searchTerm) {
      onExpandedChange(false);
    }
  };

  if (isExpanded) {
    return (
      <div className="relative w-64 transition-all duration-200">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={searchInputRef}
          type="text"
          placeholder={t('platform.cards.widgets.search')}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          onBlur={handleSearchBlur}
          className="pl-10 border-input focus:border-input focus:ring-input"
        />
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className={`p-2 hover:text-foreground hover:bg-muted ${
        searchTerm ? 'text-purple-P1' : 'text-muted-foreground'
      }`}
      onClick={handleSearchClick}
      title={t('platform.cards.widgets.search')}
    >
      <Search className="h-4 w-4" />
    </Button>
  );
}

export default SearchBar;

